<script setup lang="ts">
import { computed } from 'vue';
import { parseISO } from 'date-fns';
import { useRoute } from 'vue-router';
import { ChevronRight } from 'lucide-vue-next';
import DashboardContentLayout from '@/components/layouts/dashboardLayout/content/DashboardContentLayout.vue';
import { Button } from '@/components/ui/button';
import { useInventoryScope } from '@/composables/inventory/useInventoryScope';
import { useMedicineInventoryDetail } from '@/plugins/store/actions/queries/inventory';
import MovementsTable from '../MovementsTable.vue';
import LotsCard from './LotsCard.vue';
import PeriodFigures from './PeriodFigures.vue';
import { i18n } from '@/plugins/internationalization/i18n';

const route = useRoute();

const farmId = computed(() => Number(route.params.farmId));
const drugId = computed(() => Number(route.params.drugId));
const unitId = computed(() => Number(route.params.unitId));
const ledgerKey = computed(() => ({
    farmId: farmId.value,
    drugId: drugId.value,
    unitId: unitId.value,
}));

// The detail is scoped to the key's own farm by the route; only the period comes from the global picker.
const { scopeOfFarm } = useInventoryScope();
const scope = computed(() => scopeOfFarm(farmId.value));
const { data: detail, isError, refetch } = useMedicineInventoryDetail(scope, farmId, drugId, unitId);

const heading = computed(() => detail.value?.drug_name ?? '');
const stockStart = computed(() => (detail.value?.stock_start ? i18n.global.d(parseISO(detail.value.stock_start), 'short') : null));
</script>

<template>
    <DashboardContentLayout class="space-y-8" :heading="heading" :has-period-picker="true" :has-pig-type-picker="false">
        <template #above-heading>
            <router-link :to="{ name: 'HealthInventory' }" class="inline-flex items-center gap-x-0.5 text-sm text-gray-500 hover:underline">
                {{ $t('views.health.inventory.detail.breadcrumb') }}
                <ChevronRight class="size-3.5" aria-hidden="true" />
            </router-link>
        </template>
        <!-- The key's farm, a step larger than a plain subheading so it reads at a glance; the unit is already in every figure below. -->
        <template #subheading>
            <div v-if="detail" class="text-lg text-gray-600">{{ detail.farm_name }}</div>
            <!-- Why treatments from before the farm registered any stock are missing below: the ledger starts at its first registration. -->
            <div v-if="stockStart" class="text-sm text-gray-500">{{ $t('views.health.inventory.detail.stockStart', { date: stockStart }) }}</div>
        </template>
        <hr class="border-black border-opacity-10" />
        <div v-if="isError" class="py-14 text-center px-4 text-gray-500 text-lg space-y-4">
            <div>{{ $t('views.pigs.records.errors.couldNotLoadData') }}</div>
            <Button variant="outline" @click="refetch()">
                {{ $t('views.pigs.records.errors.retry') }}
            </Button>
        </div>
        <template v-else>
            <PeriodFigures v-if="detail" :detail="detail" />
            <LotsCard v-if="detail" :lots="detail.lots" :unit-name="detail.unit_name" />
            <MovementsTable :ledger-key="ledgerKey" />
        </template>
    </DashboardContentLayout>
</template>
